/**
 * Snackbars and confirmations — the web stand-in for `showOwnerSnack` (SnackBar) and
 * `confirmAction` (AlertDialog) in `widgets/owner_ui.dart`. Never `alert()` / `confirm()`.
 */
import { createContext, ReactNode, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useTr } from './langContext';
import { ownerStrings } from '../i18n/owner-strings';

type ToastTone = 'neutral' | 'error' | 'success';

interface Toast {
    id: number;
    message: string;
    tone: ToastTone;
}

export interface ConfirmOptions {
    title: string;
    body: string;
    confirm: string;
    cancel?: string;
    destructive?: boolean;
}

interface ToastValue {
    /** `showToast('Item saved')`, `showToast(errorText(e), 'error')`. */
    showToast: (message: string, tone?: ToastTone) => void;
    confirm: (options: ConfirmOptions) => Promise<boolean>;
}

const ToastContext = createContext<ToastValue | undefined>(undefined);

export const useToast = (): ToastValue => {
    const value = useContext(ToastContext);
    if (!value) throw new Error('useToast must be used within a ToastProvider');
    return value;
};

export function ToastProvider({ children }: { children: ReactNode }) {
    const [toasts, setToasts] = useState<Toast[]>([]);
    const [dialog, setDialog] = useState<(ConfirmOptions & { resolve: (ok: boolean) => void }) | null>(null);
    const nextId = useRef(1);
    const tr = useTr();

    const showToast = useCallback((message: string, tone: ToastTone = 'neutral') => {
        const id = nextId.current++;
        setToasts((list) => [...list, { id, message, tone }]);
        setTimeout(() => setToasts((list) => list.filter((t) => t.id !== id)), 4200);
    }, []);

    const confirm = useCallback(
        (options: ConfirmOptions) =>
            new Promise<boolean>((resolve) => {
                setDialog({ ...options, resolve });
            }),
        [],
    );

    const close = useCallback(
        (ok: boolean) => {
            dialog?.resolve(ok);
            setDialog(null);
        },
        [dialog],
    );

    const value = useMemo(() => ({ showToast, confirm }), [showToast, confirm]);

    return (
        <ToastContext.Provider value={value}>
            {children}

            <div className="pointer-events-none fixed inset-x-0 bottom-4 z-[60] flex flex-col items-center gap-2 px-4">
                {toasts.map((t) => (
                    <div
                        key={t.id}
                        className={`pointer-events-auto max-w-lg animate-slide-up rounded-xl px-4 py-3 text-sm shadow-elevated ${
                            t.tone === 'error'
                                ? 'bg-dangerbg text-dangerink'
                                : t.tone === 'success'
                                  ? 'bg-accc text-accink'
                                  : 'bg-ink text-bg'
                        }`}
                    >
                        {t.message}
                    </div>
                ))}
            </div>

            {dialog && (
                <div
                    className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 px-5 backdrop-blur-[2px]"
                    onClick={() => close(false)}
                >
                    <div
                        className="w-full max-w-sm animate-fade-in rounded-2xl border border-line bg-bg p-5"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <p className="text-[17px] font-bold text-ink">{dialog.title}</p>
                        <p className="mt-3 text-[13.5px] leading-relaxed text-ink2">{dialog.body}</p>
                        <div className="mt-5 flex justify-end gap-2">
                            <button
                                onClick={() => close(false)}
                                className="h-10 rounded-xl px-4 text-sm font-semibold text-ink2 hover:bg-surf"
                            >
                                {dialog.cancel ?? tr(ownerStrings, 'cancel')}
                            </button>
                            <button
                                onClick={() => close(true)}
                                className={`h-10 rounded-xl px-4 text-sm font-semibold ${
                                    dialog.destructive ? 'text-danger hover:bg-dangerbg' : 'text-owner hover:bg-owner-bg'
                                }`}
                            >
                                {dialog.confirm}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </ToastContext.Provider>
    );
}
